import { useState, useEffect } from "react";
import { DynamicDatePicker } from "../../../components/DatePicker";
import FilterMenu from "../../../components/FilterMenu";
import { FilterTab } from "../../../components/Filters";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  ReMixChart,
  lightenColor,
} from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import PaginationTable from "../../../components/Table";
import { RajasthanDiscomData, RajGenProfile } from "../../../models/rajasthan";
import {
  getRajasthanForecastDiscomData,
  getRajGenProfileData,
} from "../Utility/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { R_CHART_TYPES } from "../const";
import { FUNCTIONS } from "../const_raj";
import { GnaRuvnlFooter } from "../components";
import { convertToString } from "../../../extensions/date";
import { set } from "date-fns";
import { renderHourTick } from "../../../components/recharts/components";
import { DataTabs } from "./Data";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import { Info } from "lucide-react";
import MainPageContainer from "../../../components/MainPageContainer";

const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },
  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
];

const DISCOM = [
  {
    label: "JDVVNL",
    key: "JDVVNL",
  },
  {
    label: "JVVNL",
    key: "JVVNL",
  },
  {
    label: "AVVNL",
    key: "AVVNL",
  },
];

const GEN_BY = [
  {
    label: "Cumulative",
    keys: [
      {
        key: "_total_generation_procurement",
        label: "Total Gen. + Proc.",
        stackId: "gen",
      },
      { key: "_deficit", label: "Deficit", stackId: "deficit" },

      {
        key: "_deficit_percent",
        label: "Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
    ],
  },
  {
    label: "Comparative",
    keys: [
      {
        key: "_intra_state_conventional_generation",
        label: "Intra Conv. Gen.",
        stackId: "gen",
      },
      {
        key: "_intra_state_renewable_generation",
        label: "Intra RE Gen.",
        stackId: "gen",
      },
      {
        key: "_inter_state_generation",
        label: "Inter Gen.",
        stackId: "gen",
      },
      {
        key: "_long_term_agreement_lta",
        label: "LTA",
        stackId: "gen",
      },
      {
        key: "_ldc",
        label: "LDC",
        stackId: "gen",
      },
      {
        key: "_reverse_auction_deep",
        label: "RA Deep",
        stackId: "gen",
      },
      {
        key: "_total_generation_procurement",
        label: "Total Gen. + Proc.",
        stackId: "gen",
      },
      { key: "_deficit", label: "Deficit", stackId: "deficit" },
      {
        key: "_deficit_percent",
        label: "Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
    ],
  },
];

const DRAWAL_BY = [
  {
    label: "Cumulative",
    // keys: ["Total_drawal_mw"],
    keys: [
      { key: "_Total_drawal_mw", label: "Total Drawal", stackId: "drawal" },
    ],
  },
  {
    label: "Comparative",
    // keys: ["AVVNL_drawal_mw", "JVVNL_drawal_mw", "JDVVNL_drawal_mw"],
    keys: [
      { key: "_AVVNL_drawal_mw", label: "AVVNL Drawal", stackId: "drawal" },
      { key: "_JVVNL_drawal_mw", label: "JVVNL Drawal", stackId: "drawal" },
      { key: "_JDVVNL_drawal_mw", label: "JDVVNL Drawal", stackId: "drawal" },
      // { key: "_Total_drawal_mw", label: "Total Drawal", stackId: "drawal" },
    ],
  },
];

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Discom/ Area",
    key: "area",
    isTime: false,
    values: ["area", "discom"],
  },
];

const CONTENT_TYPE = ["Chart", "Table"];

const MODELS = ["Model 1", "Model 2"];

export function RajasthanDiscomForecast() {
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [genData, setGenData] = useState<RajGenProfile[]>([]);
  const [genGroupBy, setGenGroupBy] = useState(GEN_BY[0]);
  const [drawalGroupBy, setDrawalGroupBy] = useState(DRAWAL_BY[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [uniqueArea, setUniqueArea] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [selectedContentType, setSelectedContentType] = useState(
    CONTENT_TYPE[0]
  );
  const [dataTab, setDataTab] = useState(DataTabs[0]);

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedFunction, setSelectedFunction] = useState(FUNCTIONS[0]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const [dates, setDate] = useState({
    startDate: new Date("2025-01-06"),
    endDate: new Date("2025-01-13"),
    key: "selection",
  });
  async function _handlefetchData() {
    setLoading(true);
    const newData = await getRajasthanForecastDiscomData(
      convertToString(dates.startDate),
      convertToString(dates.endDate),
      selectedModel
    );
    const genData = await getRajGenProfileData({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });
    setData(newData);
    setGenData(genData);

    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
        )
      )
    );
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    setLoading(false);
  }

  function _handleGroupByChange(index: number) {
    if (selectedXaxis.isTime && selectedArea.length == 0) {
      return;
    }
    setSelectedGroupKeys([GROUP_BY[index]]);
  }
  useEffect(() => {
    let filteredData = data;

    if (selectedDiscom.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom)
      );
    }
    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date)
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.block)
      );
    }

    // filter by date
    filteredData = filteredData.filter(
      (item) =>
        new Date(item.date) >= dates.startDate &&
        new Date(item.date) <= dates.endDate
    );

    const par: ChartDataParams = {
      keys: selectedXaxis.isTime
        ? selectedBlock.length > 0 && selectedContentType == CONTENT_TYPE[0]
          ? ["date"]
          : selectedXaxis.values
        : selectedGroupKeys[0].values,
      groupBy: selectedXaxis.isTime
        ? selectedGroupKeys
            .map((k) => k.key)
            .concat(
              selectedBlock.length > 0 && selectedContentType == CONTENT_TYPE[0]
                ? ["block"]
                : []
            )
        : [],

      volumeKey: "drawal_mw",
      aggregationKeys: ["drawal_mw"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);

    console.log(cData);
    const drawal = cData.data.map((item) => {
      const AVVNL = item.AVVNL_drawal_mw ? item.AVVNL_drawal_mw / 2 : 0;
      const JVVNL = item.JVVNL_drawal_mw ? item.JVVNL_drawal_mw / 2 : 0;
      const JDVVNL = item.JDVVNL_drawal_mw ? item.JDVVNL_drawal_mw / 2 : 0;
      return {
        ...item,
        AVVNL_drawal_mw: item.AVVNL_drawal_mw ? item.AVVNL_drawal_mw / 2 : 0,
        JVVNL_drawal_mw: item.JVVNL_drawal_mw ? item.JVVNL_drawal_mw / 2 : 0,
        JDVVNL_drawal_mw: item.JDVVNL_drawal_mw ? item.JDVVNL_drawal_mw / 2 : 0,
        Total_drawal_mw: AVVNL + JVVNL + JDVVNL,
      };
    });

    console.log("Drawal", cData.keys);
    console.log("Gen Data", genData);
    const final_raw_data = (drawal.concat(genData as any) as any).map(
      (item: any) => {
        return {
          ...item,
          AVVNL_drawal_mw: item.AVVNL_drawal_mw ? item.AVVNL_drawal_mw : 0,
          JVVNL_drawal_mw: item.JVVNL_drawal_mw ? item.JVVNL_drawal_mw : 0,
          JDVVNL_drawal_mw: item.JDVVNL_drawal_mw ? item.JDVVNL_drawal_mw : 0,
          Total_drawal_mw: item.Total_drawal_mw ? item.Total_drawal_mw : 0,
          intra_state_conventional_generation:
            item.intra_state_conventional_generation
              ? item.intra_state_conventional_generation
              : 0,
          intra_state_renewable_generation:
            item.intra_state_renewable_generation
              ? item.intra_state_renewable_generation
              : 0,
          inter_state_generation: item.inter_state_generation
            ? item.inter_state_generation
            : 0,
          long_term_agreement_lta: item.long_term_agreement_lta
            ? item.long_term_agreement_lta
            : 0,
          reverse_auction_deep: item.reverse_auction_deep
            ? item.reverse_auction_deep
            : 0,
          total_generation_procurement: item.total_generation_procurement
            ? item.total_generation_procurement
            : 0,
          ldc: item.ldc ? item.ldc : 0,
        };
      }
    );
    const chartPar: ChartDataParams = {
      keys: ["date", "block"],
      groupBy: [],
      aggregationKeys: [
        "AVVNL_drawal_mw",
        "JVVNL_drawal_mw",
        "JDVVNL_drawal_mw",
        "Total_drawal_mw",
        "inter_state_generation",
        "intra_state_conventional_generation",
        "intra_state_renewable_generation",
        "ldc",
        "long_term_agreement_lta",
        "reverse_auction_deep",
        "total_generation_procurement",
        "deficit",
      ],
      priceKey: "",
      volumeKey: "",
      data: final_raw_data,
    };
    const res_data = FormatChartData(chartPar);
    const final_data = res_data.data.map((item) => {
      return {
        ...item,
        _deficit: item._Total_drawal_mw - item._total_generation_procurement,
        _deficit_percent:
          ((item._Total_drawal_mw - item._total_generation_procurement) /
            item._Total_drawal_mw) *
          100,
      };
    });
    setChartData(final_data);
    setChartKeys(cData.keys);
  }, [
    data,
    dates,
    selectedGroupKeys,
    selectedXaxis,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedChartType,
  ]);

  const tablePar = {
    legends: drawalGroupBy.keys.concat(genGroupBy.keys).map((key) => ({
      dataKey: key.key,
      name: key.label,
    })),
    data: chartData,
    unit: "mw" as "mw" | "mwh",
    extraKeys: selectedXaxis.isTime
      ? selectedBlock.length < 1
        ? ["block", "date"]
        : ["date"]
      : [selectedXaxis.values[0]],
    extraLabels: selectedXaxis.isTime
      ? selectedBlock.length < 1
        ? ["Block", "Date"]
        : ["Date"]
      : [selectedXaxis.values[0]],
  };

  useEffect(() => {
    _handlefetchData();
  }, [dates, selectedModel]);

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <div className="side-filters-container text-nowrap">
        <FilterMenu
          title="Generation By"
          items={GEN_BY.map((item) => item.label)}
          selectedItems={[genGroupBy.label]}
          onSelect={(item, index) => {
            setGenGroupBy(GEN_BY[index]);
          }}
        />
        <FilterMenu
          title="Drawal By"
          items={DRAWAL_BY.map((item) => item.label)}
          selectedItems={[drawalGroupBy.label]}
          onSelect={(item, index) => {
            setDrawalGroupBy(DRAWAL_BY[index]);
          }}
        />
        <FilterMenu
          title="Discom"
          items={DISCOM.map((item) => item.label)}
          selectedItems={selectedDiscom}
          onSelect={(item, index) => {}}
          setSelectedItems={setSelectedDiscom}
        />
        <FilterMenu
          title="Chart Type"
          items={R_CHART_TYPES.map((item) => item.label)}
          selectedItems={[selectedChartType.label]}
          onSelect={(item, index) => {
            setSelectedChartType(R_CHART_TYPES[index]);
          }}
        />
      </div>

      <MainPageContainer>
        {" "}
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Forecast (MW)</h2>
            <div className="flex gap-x-6  justify-end">
              <FilterTab
                options={DataTabs.map((item) => item.label)}
                selected={[dataTab.label]}
                onClick={(option, index) => {
                  setDataTab(DataTabs[index]);
                }}
              />

              <DynamicDatePicker
                setDate={(date) => setDate(date[0]!)!}
                defaultDates={dates}
              />
              <FilterTab
                options={MODELS}
                onClick={(option, index) => setSelectedModel(option)}
                selected={[selectedModel]}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  //

                  dataTab.label === "Summary" &&
                    downloadSummaryCSV({
                      ...tablePar,
                    });

                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 * 7 ? true : undefined}
              brushIndex={{
                startIndex: 0,
                endIndex: 96 * 7,
              }}
              isTimeSlot={selectedBlock.length < 1}
              onlyTitle={selectedBlock.length != 0}
              barCategoryGap={10}
              unit="MW"
              yAxisLabel="MW"
              secondYAxisLabel="%"
              legends={genGroupBy.keys
                .concat(drawalGroupBy.keys)
                .map((key, index) => {
                  return {
                    dataKey: key.key,
                    name: key.label,
                    legendColor: undefined,
                    stroke: lightenColor(ExchangeColors[index], 0),
                    yAxisId: key.yAxis,
                    type: selectedChartType.value,
                    stackId:
                      selectedChartType.stackId !== undefined
                        ? key.stackId
                        : undefined,
                  };
                })}
              xDataKey={selectedBlock.length < 1 ? "block" : "date"}
              secondXDataKey={
                selectedXaxis.isTime && selectedBlock.length == 0
                  ? selectedXaxis.values[1]
                  : undefined
              }
              xTick={(value: any, index: any) => {
                return renderHourTick(value, 10);
              }}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <p className="gna-footer-text flex items-center gap-2">
              <div>
                <Info size={18} className="text-primary" />
              </div>
              Note: Forecast of unrestricted demand.
            </p>

            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
